import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const PRIMARY = '#f25d27';

/**
 * @param {{ statusKey: import('../../types/support').JiraStatusCategoryKey }} props
 */
function StatusIcon({ statusKey }) {
  if (statusKey === 'done')
    return <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem', color: '#4caf50' }} />;
  if (statusKey === 'indeterminate')
    return <AutorenewIcon sx={{ fontSize: '0.95rem', color: PRIMARY }} />;
  return <HourglassEmptyIcon sx={{ fontSize: '0.95rem', color: '#bbb' }} />;
}

const PRIORITY_COLORS = {
  'Muito Alto':  { bg: '#F6D6D6', text: '#7A1515' },
  'Alto':        { bg: '#FBE7E7', text: '#A62929' },
  'Médio':       { bg: '#FFF4A1', text: '#776A0A' },
  'Baixo':       { bg: '#33C60E26', text: '#134C24' },
  'Muito Baixo': { bg: '#33C60E1A', text: '#134C24' },
  'Não definida':{ bg: '#F2F2F2', text: '#413D3D' },
};

/**
 * @param {{
 *   issue: import('../../types/support').SupportIssueViewModel;
 *   onClick: () => void;
 * }} props
 */
export default function SupportIssueCard({ issue, onClick }) {
  const priorityColor = PRIORITY_COLORS[issue.priority] || PRIORITY_COLORS['Não definida'];

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        p: '14px 16px',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        background: '#fff',
        position: 'relative',
        '&:hover': {
          borderColor: PRIMARY,
          boxShadow: `0 2px 10px ${PRIMARY}1A`,
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {/* Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <StatusIcon statusKey={issue.statusCategoryKey} />
        <Typography sx={{ fontSize: '0.72rem', color: '#999', fontWeight: 500 }}>
          {issue.statusName}
        </Typography>
      </Box>

      {/* Título */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '0.88rem',
          color: '#333',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '2.4em',
        }}
        title={issue.title}
      >
        {issue.title}
      </Typography>

      {/* Descrição */}
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: '#888',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: 1,
          minHeight: '2.4em',
        }}
        title={issue.content}
      >
        {issue.content}
      </Typography>

      {/* Rodapé */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
          <PersonOutlineIcon sx={{ fontSize: '0.82rem', color: '#ccc', flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '0.73rem',
              color: '#aaa',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Criado por: {issue.authorName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ml: 1 }}>
          <Chip
            label={issue.priority}
            size="small"
            sx={{
              fontSize: '0.68rem',
              fontWeight: 600,
              background: priorityColor.bg,
              color: priorityColor.text,
              height: 20,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: '0.82rem', color: '#ccc' }} />
            <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>{issue.comments}</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
